import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicNavbar } from '../components/layout/PublicNavbar';
import { AuroraBG } from '../components/ui/AuroraBG';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { ChefHat, CheckCircle } from 'lucide-react';

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSuccess(false);
      }
    });
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <AuroraBG />
        <PublicNavbar />
        <div className="min-h-screen flex items-center justify-center p-4">
          <GlassPanel className="w-full max-w-md p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-success-500/20 border border-success-400/50 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-success-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              Password Reset Successful!
            </h2>
            <p className="text-white/60 mb-6">
              Your password has been updated. Redirecting you to sign in...
            </p>
          </GlassPanel>
        </div>
      </>
    );
  }

  return (
    <>
      <AuroraBG />
      <PublicNavbar />
      <div className="min-h-screen flex items-center justify-center p-4 pt-20">
        <GlassPanel className="w-full max-w-md p-8">
          <div className="flex items-center justify-center mb-8">
            <ChefHat className="w-12 h-12 text-primary-400 mr-3" />
            <h1 className="text-3xl font-bold text-white">EL-OS</h1>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2 text-center">
            Set New Password
          </h2>
          <p className="text-white/60 text-sm text-center mb-6">
            Please enter your new password below.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
            />

            {error && (
              <div className="p-3 rounded-lg bg-error-500/20 border border-error-500/50 text-error-200 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </GlassPanel>
      </div>
    </>
  );
}
